import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import * as workAPI from '@App/api/routes/work'
import RootStore from '@App/stores/root-store'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import { WorkSearch } from '@App/stores/search/models/work-search'
import { generateSearchCriterionFromOption } from '@App/helpers/generate-search-criterion'
import { Works } from '@Entities/work/works'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { v4 as uuidv4 } from 'uuid'

const SEARCH_WORK_FOR_MATCH_OPTIONS: Array<SelectOptionHide<WorkSearch>> = [
    {
        id: uuidv4(),
        value: 'combinedTitles',
        label: 'Work Title',
        isPrimary: true,
        exactMatchOnly: true,
    },
    {
        id: uuidv4(),
        value: 'writerFullNames',
        label: 'Writer Name',
        exactMatchOnly: true,
    },
    { id: uuidv4(), value: 'iswc', label: 'ISWC', isPrimary: true },
    { id: uuidv4(), value: 'hfaSongCode', label: 'MLC Song Code', isPrimary: true },
    { id: uuidv4(), value: 'writerNameNumbers', label: 'Writer IPI' },
    {
        id: uuidv4(),
        value: 'originalPubNames',
        label: 'Publisher Name',
        exactMatchOnly: true,
    },
    { id: uuidv4(), value: 'originalPubIpiNameNumbers', label: 'Publisher IPI' },
    { id: uuidv4(), value: 'bothPublisherNumbers', label: 'MLC Publisher Number' },
]
const defaultMatchWorkSelectionCriteria: Array<SearchCriterion<WorkSearch>> = [
    generateSearchCriterionFromOption(SEARCH_WORK_FOR_MATCH_OPTIONS[0], true, true),
    generateSearchCriterionFromOption(SEARCH_WORK_FOR_MATCH_OPTIONS[3], true),
]

export default class WorkSearchStore {
    static createMatchWorkSearchCriteriaStore(): SearchCriteriaStore<WorkSearch> {
        const maxCriteria = 2
        return new SearchCriteriaStore(
            defaultMatchWorkSelectionCriteria,
            SEARCH_WORK_FOR_MATCH_OPTIONS,
            SearchOptionType.filter,
            RequiredCriteria.all,
            maxCriteria
        )
    }

    private rootStore: RootStore

    constructor(rootStore: RootStore) {
        makeObservable<WorkSearchStore, '_worksDraftsRightsHolder' | '_worksDraftsRightsHolderPaginationInfo'>(this, {
            _worksDraftsRightsHolder: observable,
            _worksDraftsRightsHolderPaginationInfo: observable,
            matchWorksSelectionSearchQuery: computed,
            getWorksDraftsRightsHolder: action,
            destroyWorksDraftsRightsHolderPaginationInfo: action,
        })

        this.rootStore = rootStore
    }

    private _worksDraftsRightsHolder: Works | undefined

    private _worksDraftsRightsHolderPaginationInfo: PaginationInfo | undefined

    private _matchWorksSelectionQuery: WorkSearch | undefined

    get matchWorksSelectionSearchQuery(): WorkSearch | undefined {
        return this._matchWorksSelectionQuery
    }

    async getWorksDraftsRightsHolder(rightsHolderId: number, pagination: PaginationBody): Promise<void> {
        try {
            const axiosResponse = await workAPI.worksDraftsRH(rightsHolderId, pagination)
            const response: Works = axiosResponse.data

            runInAction(() => {
                this._worksDraftsRightsHolder = response

                if (pagination.limit && pagination.page && response) {
                    this._worksDraftsRightsHolderPaginationInfo = calcNumPages(
                        response.count,
                        pagination.limit,
                        pagination.page
                    )
                }
            })
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
        }
    }

    get worksDraftsRightsHolder(): Works | undefined {
        return this._worksDraftsRightsHolder
    }

    get worksDraftsRightsHolderPaginationInfo(): PaginationInfo | undefined {
        return this._worksDraftsRightsHolderPaginationInfo
    }

    destroyWorksDraftsRightsHolderPaginationInfo(): void {
        this._worksDraftsRightsHolderPaginationInfo = undefined
    }

    setMatchWorksSelectionSearchQuery(matchWorksSelectionSearchQuery: WorkSearch): void {
        this._matchWorksSelectionQuery = matchWorksSelectionSearchQuery
    }
}
